/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";

import { api } from "@/lib/api";
import { toast } from "sonner";
import { track, trackOnce } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import AssigneePicker from "@/components/AssigneePicker";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Building2, Wrench, MapPin, DoorOpen, Car, Building, List as ListIcon, AlertTriangle,
  Ruler, Cpu, Plus, X,
} from "lucide-react";

const MEASUREMENT_UNIT_SUGGESTIONS = ["°C", "Pa", "kPa", "bar", "l/s", "m³/h", "V", "A", "Ω", "kWh", "W", "kW", "mm", "cm", "m", "%", "ppm", "dB", "lx", "rpm"];

const KIND_ICON = {
  huoneistot: Building2,
  laitteet: Wrench,
  tarkastuspisteet: MapPin,
  tilat: DoorOpen,
  ajoneuvot: Car,
  rakennukset: Building,
  mukautettu: ListIcon,
};

const PROPERTY_SUGGESTIONS_FI = ["Kerrostalo", "Rivitalo", "Omakotitalo", "Liiketila", "Teollisuuskohde", "Mökki", "Varasto", "Muu"];
const PROPERTY_SUGGESTIONS_EN = ["Apartment block", "Row house", "Detached house", "Retail space", "Industrial site", "Cottage", "Warehouse", "Other"];

export default function ReportWizard() {
  const [params] = useSearchParams();
  const location = useLocation();
  const initialFolder = params.get("folder") || null;
  const initialProperty = location.state?.property_id || null;

  const [step, setStep] = useState(0);
  const [unitKinds, setUnitKinds] = useState([]);
  const [companyChecklists, setCompanyChecklists] = useState([]);
  const [companyMaterials, setCompanyMaterials] = useState([]);
  const [contentTemplates, setContentTemplates] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [sel, setSel] = useState({
    work_type: "",
    property_type: "",
    unit_kind: null,
    unit_label_template: "",
    unit_count: 5,
    custom_units: "",
    base_checklist: [],
    base_checklist_findings: [], // labels that should be flagged as findings
    materials: [], // [{name, qty, unit}]
    measurement_fields: [], // [{label, unit}]
    device_fields: [], // [{label}]
    custom_fields: [], // [{id, label, type, options, hint}] — sisältöpohjasta
    customer_name: "",
    site_address: "",
    executor_company: "",
    ask_executor: true,
    image_timestamps: true,
    request_signature: true,
    hide_work_checklists: false,
    hide_finding_checklists: false,
    hide_materials: false,
    hide_kpi_cards: false,
    property_id: initialProperty,
    folder_id: initialFolder,
    contact: { name: "", phone: "", email: "", address: "", notes: "" },
    assignees: [],
  });
  const [newCheckItem, setNewCheckItem] = useState("");
  const [newFindingItem, setNewFindingItem] = useState("");
  const [newMaterialName, setNewMaterialName] = useState("");
  const [newMaterialUnit, setNewMaterialUnit] = useState("kpl");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isEn } = useT();
  const PROPERTY_SUGGESTIONS = isEn ? PROPERTY_SUGGESTIONS_EN : PROPERTY_SUGGESTIONS_FI;

  // Localized helpers for unit kinds (backend returns FI labels).
  const kindLabel = (k) => t(`wiz.kind.${k.id}`, k.label);
  const kindTemplate = (k) => {
    const key = `wiz.kind.${k.id}.tpl`;
    const v = t(key);
    if (v !== key) return v;
    return k.default_template || t("wiz.kind.default.tpl");
  };

  useEffect(() => {
    api.get("/catalog/unit-kinds").then(({ data }) => setUnitKinds(data.unit_kinds));
    api.get("/company/checklists").then(({ data }) => setCompanyChecklists(data.checklists || [])).catch(() => {});
    api.get("/company/materials").then(({ data }) => setCompanyMaterials(data.materials || [])).catch(() => {});
    api.get("/content-templates").then(({ data }) => setContentTemplates(data.templates || [])).catch(() => {});
    // Pre-fill executor_company from user profile
    api.get("/auth/me").then(({ data }) => {
      if (data?.company_name) setSel((s) => ({ ...s, executor_company: s.executor_company || data.company_name }));
    }).catch(() => {});
  }, []);

  const addCheckItem = () => {
    const t = newCheckItem.trim();
    if (!t) return;
    if (sel.base_checklist.includes(t)) { setNewCheckItem(""); return; }
    setSel((s) => ({ ...s, base_checklist: [...s.base_checklist, t] }));
    setNewCheckItem("");
  };
  const addFindingItem = () => {
    const t = newFindingItem.trim();
    if (!t) return;
    if (sel.base_checklist.includes(t)) {
      // Already exists — promote to finding if it isn't already
      if (!(sel.base_checklist_findings || []).includes(t)) {
        setSel((s) => ({ ...s, base_checklist_findings: [...(s.base_checklist_findings || []), t] }));
      }
      setNewFindingItem("");
      return;
    }
    setSel((s) => ({
      ...s,
      base_checklist: [...s.base_checklist, t],
      base_checklist_findings: [...(s.base_checklist_findings || []), t],
    }));
    setNewFindingItem("");
  };
  const removeCheckItem = (i) => {
    setSel((s) => {
      const removed = s.base_checklist[i];
      return {
        ...s,
        base_checklist: s.base_checklist.filter((_, idx) => idx !== i),
        base_checklist_findings: (s.base_checklist_findings || []).filter((x) => x !== removed),
      };
    });
  };
  const toggleFindingFlag = (label) => {
    setSel((s) => {
      const cur = s.base_checklist_findings || [];
      return {
        ...s,
        base_checklist_findings: cur.includes(label)
          ? cur.filter((x) => x !== label)
          : [...cur, label],
      };
    });
  };
  const applyCompanyChecklist = (cl) => {
    const merged = [...sel.base_checklist];
    const findingsMerged = [...(sel.base_checklist_findings || [])];
    // `base_checklist` on KAIKKIEN itemien lista (sekä työ että havainnot).
    // `base_checklist_findings` on SUBSET-merkintä joka itemeistä on havainto.
    // Kaikki labelit lisätään base_checklist:iin — finding-itemit lisäksi
    // findings-listaan jotta UI näyttää ne amber-värisinä havaintoina.
    (cl.items || []).forEach((it) => {
      const label = typeof it === "string" ? it : it?.label;
      if (!label) return;
      const itemIsFinding = typeof it === "object" ? !!it.is_finding : cl.kind === "findings";
      if (!merged.includes(label)) merged.push(label);
      if (itemIsFinding && !findingsMerged.includes(label)) findingsMerged.push(label);
    });
    setSel((s) => ({ ...s, base_checklist: merged, base_checklist_findings: findingsMerged }));
    toast.success(`${t("wiz.toast.checklistAdded")} "${cl.name}" — ${(cl.items || []).length} ${t("wiz.toast.steps")}`);
  };
  const toggleCompanyMaterial = (m) => {
    const already = sel.materials.find((x) => x.name === m.name);
    if (already) {
      setSel((s) => ({ ...s, materials: s.materials.filter((x) => x.name !== m.name) }));
    } else {
      setSel((s) => ({ ...s, materials: [...s.materials, { name: m.name, unit: m.unit }] }));
    }
  };
  const addCustomMaterial = () => {
    const name = newMaterialName.trim();
    if (!name) return;
    if (sel.materials.find((x) => x.name === name)) { setNewMaterialName(""); return; }
    setSel((s) => ({ ...s, materials: [...s.materials, { name, unit: newMaterialUnit || "kpl" }] }));
    setNewMaterialName("");
  };
  const removeMaterial = (name) => {
    setSel((s) => ({ ...s, materials: s.materials.filter((m) => m.name !== name) }));
  };

  const currentKind = unitKinds.find((k) => k.id === sel.unit_kind);
  const customUnitsList = sel.custom_units.split("\n").map((s) => s.trim()).filter(Boolean);

  const next = () => setStep((s) => Math.min(s + 1, 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext =
    (step === 0 && (sel.work_type || "").trim().length > 0) ||
    (step === 1 && sel.unit_kind && (sel.unit_kind !== "mukautettu" ? sel.unit_count > 0 : customUnitsList.length > 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        industry: "muu", // free / no industry binding
        property_type: (sel.property_type || "").trim(),
        work_type: sel.work_type.trim(),
        unit_kind: sel.unit_kind,
        title: sel.work_type.trim() || undefined,
        customer_name: sel.customer_name || undefined,
        site_address: sel.site_address || undefined,
        executor_company: sel.executor_company || undefined,
        ask_executor: sel.ask_executor,
        folder_id: sel.folder_id || undefined,
        property_id: sel.property_id || undefined,
        base_checklist: sel.base_checklist.length > 0 ? sel.base_checklist : undefined,
        base_checklist_findings: (sel.base_checklist_findings || []).length > 0 ? sel.base_checklist_findings : undefined,
        image_timestamps: sel.image_timestamps,
        request_signature: sel.request_signature,
        hide_work_checklists: sel.hide_work_checklists,
        hide_finding_checklists: sel.hide_finding_checklists,
        hide_materials: sel.hide_materials,
        hide_kpi_cards: sel.hide_kpi_cards,
        materials: sel.materials.length > 0 ? sel.materials : undefined,
        measurement_fields: sel.measurement_fields.length > 0
          ? sel.measurement_fields.filter((f) => (f.label || "").trim()).map((f) => ({ label: f.label.trim(), unit: (f.unit || "").trim() }))
          : undefined,
        device_fields: sel.device_fields.length > 0
          ? sel.device_fields.filter((f) => (f.label || "").trim()).map((f) => ({ label: f.label.trim() }))
          : undefined,
        custom_fields: (sel.custom_fields || []).length > 0 ? sel.custom_fields : undefined,
        assignees: (sel.assignees || []).length > 0 ? sel.assignees : undefined,
        contact: (() => {
          const c = sel.contact || {};
          const cleaned = {
            name: (c.name || "").trim() || null,
            phone: (c.phone || "").trim() || null,
            email: (c.email || "").trim() || null,
            address: (c.address || "").trim() || null,
            notes: (c.notes || "").trim() || null,
          };
          return Object.values(cleaned).some(Boolean) ? cleaned : undefined;
        })(),
      };
      if (sel.unit_kind === "mukautettu") {
        payload.custom_units = customUnitsList;
        payload.unit_count = customUnitsList.length;
      } else {
        payload.unit_count = Number(sel.unit_count);
        if (sel.unit_label_template?.trim()) payload.unit_label_template = sel.unit_label_template.trim();
      }
      const { data } = await api.post("/reports", payload);

      // Activation analytics — fire on every long-report creation, and
      // a one-shot "First Report Created" event on the user's very first one.
      track("Report Created", { type: "long" });
      if (user?.user_id) {
        trackOnce("First Report Created", user.user_id, { type: "long" });
      }

      toast.success(t("wiz.toast.created"));
      navigate(`/app/raportti/${data.report_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wiz.toast.createFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [t("wiz.steps.work"), t("wiz.steps.units")];

  const previewLabels = (() => {
    if (sel.unit_kind === "mukautettu") return customUnitsList.slice(0, 6);
    if (!currentKind) return [];
    const tpl = (sel.unit_label_template?.trim() || kindTemplate(currentKind));
    const n = Number(sel.unit_count) || 0;
    return Array.from({ length: Math.min(n, 6) }, (_, i) => `${tpl} ${i + 1}`);
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="wizard-back-dashboard">
          <ArrowLeft className="h-4 w-4" /> {t("wiz.back")}
        </button>

        <div className="grid grid-cols-1 gap-8 items-start">
          <div>
            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">{t("wiz.eyebrow")}</div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">
                {t("wiz.heading")}
              </h1>
            </div>

            <div className="flex items-center gap-2 mb-3">
              {steps.map((_, i) => (
                <div key={i} className={`h-2 rounded-full flex-1 transition-colors ${i <= step ? "bg-emerald-500" : "bg-slate-200"}`} />
              ))}
            </div>
            <div className="mb-6 text-sm font-semibold text-slate-500">
              {t("wiz.step")} {step + 1}/{steps.length} · {steps[step]}
            </div>

            <div className="card-soft p-5 md:p-10" data-testid={`wizard-step-${step}`}>
          {/* STEP 0: Työ */}
          {step === 0 && (
            <>
              <h2 className="font-display text-2xl font-semibold text-slate-900">{t("wiz.s0.title")}</h2>
              <p className="text-slate-600 mt-1">{t("wiz.s0.sub")}</p>

              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("wiz.s0.workName")}</label>
                <input
                  autoFocus
                  value={sel.work_type}
                  onChange={(e) => setSel({ ...sel, work_type: e.target.value })}
                  className="input-base"
                  placeholder={t("wiz.s0.workPlaceholder")}
                  data-testid="wizard-work-input"
                />
              </div>

              {/* Kohdenna käyttäjälle — nostettu ylös heti työn nimen alle,
                  jotta työnjohto näkee kuka tulee työskentelemään ennen kuin
                  jatkaa yksityiskohtiin (iter78 UX-säätö). */}
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-sm font-semibold text-slate-900">Kohdenna käyttäjälle <span className="text-slate-400 font-normal">(valinnainen)</span></div>
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  Valitut työntekijät näkevät tämän työn /kentta-näkymässä.
                </p>
                <AssigneePicker
                  value={sel.assignees}
                  onChange={(ids) => setSel({ ...sel, assignees: ids })}
                  testIdPrefix="wizard-assignees"
                />
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t("wiz.s0.propType")} <span className="text-slate-400 font-normal">{t("wiz.s0.propTypeOptional")}</span>
                </label>
                <input
                  value={sel.property_type}
                  onChange={(e) => setSel({ ...sel, property_type: e.target.value })}
                  className="input-base"
                  placeholder={t("wiz.s0.propTypePlaceholder")}
                  data-testid="wizard-property-input"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {PROPERTY_SUGGESTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setSel({ ...sel, property_type: p })}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        sel.property_type === p
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid={`wizard-property-suggest-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Työntekijät (valinnainen) — määrää yksi tai useampi kenttätyöntekijä
                  työlle. He näkevät työn /kentta-näkymässään. Pakottamaton: voit
                  jättää tyhjäksi ja määrätä myöhemmin raporttidetaljista. */}

              {/* Työpohja (valinnainen) — täyttää työvaiheet, havaintolistat,
                  tarvikkeet ja muut kerättävät tiedot yhdellä klikillä */}
              {contentTemplates.length > 0 && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">Työpohja <span className="text-slate-400 font-normal">(valinnainen)</span></div>
                      <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        Aloita aiemmin tallentamastasi työpohjasta — työvaiheet, havaintolistat, tarvikkeet ja muut tiedot esitäytetään.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        setAppliedTemplateId(null);
                        setSel((s) => ({
                          ...s,
                          base_checklist: [],
                          base_checklist_findings: [],
                          materials: [],
                          custom_fields: [],
                          measurement_fields: [],
                        }));
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        !appliedTemplateId
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="wizard-template-none"
                    >
                      Aloita tyhjästä
                    </button>
                    {contentTemplates.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          setAppliedTemplateId(tpl.id);
                          setSel((s) => ({
                            ...s,
                            base_checklist: [...(tpl.base_checklist || [])],
                            base_checklist_findings: [...(tpl.base_checklist_findings || [])],
                            materials: (tpl.materials || []).map((m) => ({ name: m.name, unit: m.unit || "kpl" })),
                            custom_fields: [...(tpl.custom_fields || [])],
                            measurement_fields: [...(tpl.measurement_fields || [])],
                            // Peri myös raporttiasetukset työpohjasta (jos määritelty)
                            unit_kind: tpl.unit_kind ?? s.unit_kind,
                            unit_label_template: tpl.unit_label_template ?? s.unit_label_template,
                            property_type: tpl.property_type ?? s.property_type,
                            image_timestamps: tpl.image_timestamps ?? s.image_timestamps,
                            request_signature: tpl.request_signature ?? s.request_signature,
                            ask_executor: tpl.ask_executor ?? s.ask_executor,
                            hide_work_checklists: tpl.hide_work_checklists ?? s.hide_work_checklists,
                            hide_finding_checklists: tpl.hide_finding_checklists ?? s.hide_finding_checklists,
                            hide_materials: tpl.hide_materials ?? s.hide_materials,
                            hide_kpi_cards: tpl.hide_kpi_cards ?? s.hide_kpi_cards,
                          }));
                          toast.success(`Työpohja "${tpl.name}" käytössä`);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          appliedTemplateId === tpl.id
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`wizard-template-${tpl.id}`}
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* STEP 1: Yksiköt */}
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-semibold text-slate-900">{t("wiz.s1.title")}</h2>
              <p className="text-slate-600 mt-1">{t("wiz.s1.sub")}</p>

              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-6 mb-2">{t("wiz.s1.pickKind")}</div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {unitKinds.map((k) => {
                  const Icon = KIND_ICON[k.id] || ListIcon;
                  const active = sel.unit_kind === k.id;
                  return (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setSel({ ...sel, unit_kind: k.id, unit_label_template: kindTemplate(k) })}
                      className={`text-left card-soft p-4 border-2 ${active ? "border-emerald-500 ring-4 ring-emerald-100" : "border-transparent"}`}
                      data-testid={`wizard-kind-${k.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="font-display font-semibold text-slate-900 text-sm">{kindLabel(k)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {sel.unit_kind && sel.unit_kind !== "mukautettu" && (
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("wiz.s1.labelPrefix")}</label>
                    <input
                      value={sel.unit_label_template}
                      onChange={(e) => setSel({ ...sel, unit_label_template: e.target.value })}
                      className="input-base"
                      placeholder={currentKind ? kindTemplate(currentKind) : t("wiz.kind.default.tpl")}
                      data-testid="wizard-label-template"
                    />
                    <div className="text-xs text-slate-500 mt-1">
                      {(() => {
                        const tpl = currentKind ? kindTemplate(currentKind) : t("wiz.kind.default.tpl");
                        return `${isEn ? "e.g." : "Esim."} "${tpl}" → "${tpl} 1", "${tpl} 2"...`;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("wiz.s1.count")}</label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={sel.unit_count}
                      onChange={(e) => setSel({ ...sel, unit_count: e.target.value })}
                      className="input-base"
                      data-testid="wizard-unit-count-input"
                    />
                  </div>
                </div>
              )}

              {sel.unit_kind === "mukautettu" && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("wiz.s1.customLabel")}
                  </label>
                  <textarea
                    value={sel.custom_units}
                    onChange={(e) => setSel({ ...sel, custom_units: e.target.value })}
                    rows={8}
                    placeholder={t("wiz.customPlaceholder")}
                    className="input-base font-mono text-sm"
                    data-testid="wizard-custom-units"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    {customUnitsList.length} {customUnitsList.length !== 1 ? t("dash.unitMany") : t("dash.unitOne")} – {t("wiz.s1.customHint")}
                  </div>
                </div>
              )}

              {previewLabels.length > 0 && (
                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{t("wiz.s1.preview")}</div>
                  <div className="flex flex-wrap gap-2">
                    {previewLabels.map((l, i) => (
                      <span key={i} className="badge badge-emerald">{l}</span>
                    ))}
                    {((sel.unit_kind === "mukautettu" ? customUnitsList.length : Number(sel.unit_count) || 0) > 6) && (
                      <span className="badge badge-slate">
                        +{(sel.unit_kind === "mukautettu" ? customUnitsList.length : Number(sel.unit_count) || 0) - 6} {t("wiz.s1.moreLabel")}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

        </div>

        <div className="mt-6 flex items-center justify-between">
          <button type="button" onClick={back} disabled={step === 0} className="btn-ghost disabled:opacity-40" data-testid="wizard-back-button">
            <ArrowLeft className="h-4 w-4" /> {t("wiz.previous")}
          </button>
          {step < 1 ? (
            <button type="button" onClick={next} disabled={!canNext} className="btn-primary disabled:opacity-50" data-testid="wizard-next-button">
              {t("wiz.next")} <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting || !canNext} className="btn-primary disabled:opacity-50" data-testid="wizard-submit-button">
              {submitting ? t("wiz.creating") : t("wiz.create")} <CheckCircle2 className="h-4 w-4" />
            </button>
          )}
        </div>

          {/* Sulkee vasemman sarakkeen */}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdvOpt({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 px-3.5 py-3 cursor-pointer hover:bg-white">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-emerald-600"
        data-testid={testId}
      />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      </div>
    </label>
  );
}


/* -------------------------------------------------------------------------- */
/* FieldEditor — käyttäjä lisää mittauskenttiä tai laitetietokenttiä          */
/* raportille. `withUnit=true` näyttää yksikkösarakkeen ja ehdotukset.        */
/* -------------------------------------------------------------------------- */
function FieldEditor({ fields, onChange, withUnit = false, unitSuggestions = [], addPlaceholder, unitPlaceholder = "Yksikkö", testIdPrefix }) {
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");

  const add = () => {
    const label = newLabel.trim();
    if (!label) return;
    const next = [...fields, withUnit ? { label, unit: newUnit.trim() } : { label }];
    onChange(next);
    setNewLabel("");
    setNewUnit("");
  };
  const remove = (idx) => onChange(fields.filter((_, i) => i !== idx));

  return (
    <div>
      {fields.length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid={`${testIdPrefix}-fields-list`}>
          {fields.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid={`${testIdPrefix}-field-${i}`}>
              <span className="flex-1 text-sm text-slate-800 font-medium">{f.label}</span>
              {withUnit && f.unit && (
                <span className="text-xs text-emerald-700 font-semibold px-2 py-0.5 rounded-full bg-white border border-emerald-200">{f.unit}</span>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="p-1 rounded text-slate-400 hover:text-red-600"
                data-testid={`${testIdPrefix}-field-remove-${i}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={withUnit ? "grid grid-cols-[1fr_100px_auto] gap-2" : "flex gap-2"}>
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={addPlaceholder}
          className="input-base"
          data-testid={`${testIdPrefix}-new-label`}
        />
        {withUnit && (
          <input
            value={newUnit}
            onChange={(e) => setNewUnit(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder={unitPlaceholder}
            className="input-base"
            list={`${testIdPrefix}-units`}
            data-testid={`${testIdPrefix}-new-unit`}
          />
        )}
        <button
          type="button"
          onClick={add}
          disabled={!newLabel.trim()}
          className="btn-primary px-4 disabled:opacity-50"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="h-4 w-4 inline-block" />
        </button>
      </div>
      {withUnit && unitSuggestions.length > 0 && (
        <>
          <datalist id={`${testIdPrefix}-units`}>
            {unitSuggestions.map((u) => <option key={u} value={u} />)}
          </datalist>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {unitSuggestions.slice(0, 10).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setNewUnit(u)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                  newUnit === u ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`${testIdPrefix}-unit-suggest-${u}`}
              >
                {u}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
